/**
 * Team Activity Domain
 *
 * Manages the server-controlled activity state during a session script step.
 * An Activity is a composable pipeline of phases (synced_dialogue, selection,
 * speaking_rounds, voting, results) configured in the session script JSON file.
 *
 * The server sends:
 *   - OpCodeTeamActivity (330): full state whenever the activity changes
 *
 * The client sends:
 *   - OpCodeTeamActivityAction (331): player action (select, vote, etc.)
 *
 * Adding a new phase type only requires:
 *   1. Implementing PhaseHandler in Go (server)
 *   2. A view on the activity's sheet, placed in team-activity-overlay.tsx
 *      (SESSION-SCRIPT-SYSTEM.md §13)
 */

import { openDialogue } from './dialogue';
import { create } from 'zustand';
import type { TeamActivityPayload } from '../constants/op-codes';
import { OpCode, MessageType } from '../constants/op-codes';
import { isGraded, loadQuiz, longEnough, triesFor } from '../data/quizzes/quiz-loader';
import { currentSeconds, useNowSeconds } from '../utils/use-now-seconds';
import { getViewerUserId } from './current-team';
import { useVoiceChatStore } from './voice-chat';
import { sendToMatch } from '../services/nakama/match-send';

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * A phase's clock as this client keeps it — the selection's, a speaking turn's,
 * the vote's, the result's, a quiz question's. The server sends how much is
 * left whenever the phase changes, and how long the clock runs; the client
 * counts down between, from when it runs out on its own clock.
 */
interface ActivityClock {
  /** When the time runs out, in whole Unix seconds on this machine. */
  endsAt: number;
  /** How long it runs, in seconds: the phase's, the turn's or the question's limit. */
  length: number;
  /** Which clock it is — the phase, and the turn or the question and its try — so a new one starts afresh. */
  key: string;
}

interface TeamActivityState {
  /** Whether an activity is currently in progress. */
  active: boolean;
  /** Current 0-based phase index. */
  phaseIndex: number;
  /** Total number of phases in this activity. */
  phaseCount: number;
  /** String type of the current phase (e.g. "synced_dialogue", "selection"). */
  phaseType: string | null;
  /** Phase-specific state data from the server (timers, speaker, selections…). */
  phaseState: Record<string, unknown>;
  /**
   * Cross-phase shared data (e.g. selections made in an earlier phase that are
   * needed by the voting and results phases).
   */
  sharedData: Record<string, unknown>;
  /** True when the activity has finished all phases. */
  complete: boolean;

  /** The current phase's clock, or null when it has none. */
  clock: ActivityClock | null;

  /**
   * When true, the globe is in "selection mode": clicking an airport proposes
   * it as the player's city instead of opening the city panel. Only while a
   * `selection` phase for the HQ runs and the player has not yet chosen.
   */
  globeSelectionModeActive: boolean;

  /**
   * The city the player has clicked on the globe in a selection phase and not
   * yet confirmed: the choice is final only once confirmed, since the server
   * keeps the first city it is sent. Cleared as the phase changes.
   */
  pendingPick: string | null;

  /** The player has confirmed their pick and it is on its way, until the server lists it. */
  pickSent: boolean;

  /**
   * The city the player voted for in this vote. The server says who has
   * voted, never for what, until the vote closes, so the client keeps what
   * it sent: its ballot shows it, and rests busy until the server lists the
   * player among those who voted. Cleared as the phase changes.
   */
  voteSent: string | null;

  /**
   * Set to true immediately when the local player closes the synced_dialogue,
   * before the server round-trip confirms it. This allows the waiting screen to
   * appear without delay. Cleared when the phase advances.
   */
  localDialogueSeen: boolean;

  /**
   * When true, the activity is running in local preview mode (triggered from
   * admin cheats). Actions are processed locally without a server round-trip.
   */
  previewMode: boolean;

  // ── Actions ──────────────────────────────────────────────────────────────
  /** Takes the server's state as `viewerId` is sent it, `now` in whole Unix seconds. */
  setActivityState: (payload: TeamActivityPayload, viewerId: string | undefined, now: number) => void;
  reset: () => void;
  resetCompletion: () => void;
  markLocalDialogueSeen: () => void;
  /**
   * Start a local quiz preview for development/testing.
   * Sets up a fake quiz activity state without a server connection.
   * @param quizId - The quiz definition ID (e.g. "01-session-script-intro-session/debrief-quiz")
   * @param localUserId - The local player's user ID for the fake player state
   */
  startQuizPreview: (quizId: string, localUserId: string) => void;
  /** Dismiss the local preview and reset all activity state. */
  stopPreview: () => void;
  /**
   * Process a quiz action locally (preview mode only).
   * Simulates server-side answer validation using the client-side quiz definition.
   */
  processPreviewAction: (action: Record<string, unknown>) => void;
}

/**
 * The verdict on a player's latest graded quiz answer, as the server sends it
 * in their `last_answer_result` (phase-quiz.go): right, wrong with a try left
 * (`attempt < max_attempts`), or wrong on the last try. Opinion and free-text
 * questions have none.
 */
export interface QuizVerdict {
  /** The question answered — the one before the player's current question once they have moved on. */
  question_index: number;
  correct: boolean;
  /** Which try this answer was, from 1. */
  attempt: number;
  max_attempts: number;
}

/** One player's city in a selection phase (phase-selection.go, SelectionEntry). */
interface SelectionEntry {
  value: string;
  /** Drawn for them at random when the time ran out, not chosen. */
  was_random: boolean;
}

/**
 * Every player's city, as the selection phase sends it and the phases after it
 * carry it in `shared_data.selections`: null for a player still choosing.
 */
export type Selections = Record<string, SelectionEntry | null>;

/**
 * The id a city drawn at random stands under in `Selections` when no one was
 * online to put one forward (phase-selection.go, RandomSelection; held to it
 * by client_parity_test.go). It is no player: no roll, ballot or badge names it.
 */
export const RANDOM_SELECTION = '__random__';

/** Everyone the selection listed — those who chose and those a city was drawn for — the stand-in left out. */
export function choosersOf(selections: Selections): string[] {
  return Object.keys(selections).filter((id) => id !== RANDOM_SELECTION);
}

/** Who put `code` forward, in the order the selection lists them. */
export function suggestersOf(selections: Selections, code: string): string[] {
  return choosersOf(selections).filter((id) => selections[id]?.value === code);
}

/** A selection phase's state (phase-selection.go): each player choosing a city. */
export interface SelectionState {
  subject?: string;
  time_remaining: number;
  time_limit?: number;
  /** A player still without a city when the time runs out is given one at random. */
  random_fallback?: boolean;
  selections: Selections;
}

/** A vote's state (phase-voting.go): who has voted, never for what, until it closes. */
export interface VotingState {
  /** Every city put forward, by airport code, sorted. */
  vote_options: string[];
  voted_player_ids: string[];
  time_remaining: number;
  /** How long the vote runs: every vote has a clock, or its script does not load (phase-voting.go, CheckConfig). */
  time_limit?: number;
}

/** One player's vote, as the closed vote shares it (phase-voting.go, VoteEntry). */
export interface VoteEntry {
  voted_for: string;
  /** Cast for them at random when the time ran out. */
  was_random: boolean;
}

/** The city the team chose (phase-voting.go, WinnerEntry). */
interface WinnerEntry {
  value: string;
  vote_count: number;
  /** Level at the top with another: the server drew the winner among them. */
  was_tiebreak: boolean;
}

/** The result's state (phase-results.go): every city put forward and its votes, shown for a while. */
export interface ResultsState {
  winner: WinnerEntry | null;
  /** Every city put forward, those with none included. */
  vote_counts: Record<string, number>;
  selections: Selections;
  time_remaining: number;
  time_limit?: number;
}

/** One player's place in a quiz, as the quiz's state carries it (phase-quiz.go). */
export interface QuizPlayerState {
  current_question_index: number;
  answered_count: number;
  /** Done with every question — or released by a disconnect. */
  complete: boolean;
  time_remaining?: number;
  time_limit?: number;
  /** The verdict on their latest graded answer, until their next answer or timeout. */
  last_answer_result?: QuizVerdict;
}

/**
 * A quiz's state (phase-quiz.go): each player answering alone. It is one
 * payload for the team, so it carries teammates' verdicts too; a player is
 * shown only their own.
 */
export interface QuizState {
  quiz_id: string;
  question_count: number;
  players: Record<string, QuizPlayerState>;
}

/**
 * A dialogue the team reads before it moves on (phase-synced-dialogue.go, and
 * phase-per-player-synced-dialogue.go, where each reads their own): who has
 * closed it.
 */
interface DialogueState {
  /** Everyone the phase waits on, done or not: a player who drops counts as done. */
  all_players: Record<string, boolean>;
  seen_by?: Record<string, boolean>;
}

/** The achievement phase's state (phase-earn-achievement.go): who earned what, and who is done with it. */
export interface AchievementState {
  /** Achievement ids by the player who earned them; it reaches the whole team. */
  earned_by: Record<string, string[]>;
  acknowledged_by: Record<string, boolean>;
  /** Everyone, done or not: whoever earned nothing is done at once. */
  all_players: Record<string, boolean>;
}

/** A speaking round's state (phase-speaking-rounds.go): the floor, a turn at a time. */
export interface SpeakingState {
  /** Everyone with a turn, in the order the server shuffled. */
  speaker_order: string[];
  /** Whoever has the floor; empty once the last turn is over. */
  current_speaker_id: string;
  current_turn_index: number;
  turn_time_remaining: number;
  turn_time_limit?: number;
}

// ── The phase's clock ──────────────────────────────────────────────────────

/** What one state says of its phase's clock: how much is left, how long it runs, and which it is. */
interface ClockReading {
  remaining: number;
  length: number;
  key: string;
}

function reading(remaining: unknown, length: unknown, key: string): ClockReading | null {
  if (typeof remaining !== 'number' || typeof length !== 'number' || length <= 0) return null;
  return { remaining: Math.max(0, remaining), length, key };
}

/**
 * Which try at their question a quiz player is on: the one after the verdict
 * on it, or the first when they have none on it.
 */
export function currentTry(player: Partial<Pick<QuizPlayerState, 'current_question_index' | 'last_answer_result'>>): number {
  return (verdictForQuestion(player.last_answer_result, player.current_question_index ?? 0)?.attempt ?? 0) + 1;
}

/**
 * The clock in a state as `viewerId` is sent it: the phase's, a turn's, or —
 * in a quiz, where each player answers alone — the viewer's question's, a new
 * one with each try. Null for a phase with no clock, or a state that sends
 * no length.
 */
export function clockReading(
  payload: Pick<TeamActivityPayload, 'phase_type' | 'phase_index' | 'phase_state' | 'complete'>,
  viewerId: string | undefined,
): ClockReading | null {
  const state = payload.phase_state ?? {};
  if (payload.complete) return null;
  switch (payload.phase_type) {
    case 'selection':
    case 'voting':
    case 'results':
      return reading(state.time_remaining, state.time_limit, `${payload.phase_index}`);
    case 'speaking_rounds':
      return reading(state.turn_time_remaining, state.turn_time_limit, `${payload.phase_index}:${state.current_turn_index}`);
    case 'quiz': {
      const players = (state.players ?? {}) as Record<string, Partial<QuizPlayerState>>;
      const player = viewerId ? players[viewerId] : undefined;
      if (!player || player.complete) return null;
      return reading(
        player.time_remaining,
        player.time_limit,
        `${payload.phase_index}:${player.current_question_index}:${currentTry(player)}`,
      );
    }
    default:
      return null;
  }
}

/**
 * The clock to keep, given the one kept and a new reading at `now`. The
 * server counts in whole seconds, and so does this client, a little apart: a
 * reading of the same clock within a second of the one kept keeps it, so the
 * countdown does not jump back and forth as states arrive. A new clock, or one
 * further out — a second try's, which starts afresh — is taken as it is.
 */
export function nextClock(kept: ActivityClock | null, read: ClockReading | null, now: number): ActivityClock | null {
  if (!read) return null;
  const endsAt = now + read.remaining;
  if (kept && kept.key === read.key && kept.length === read.length && Math.abs(kept.endsAt - endsAt) <= 1) return kept;
  return { endsAt, length: read.length, key: read.key };
}

/** The phase's clock now: the seconds left and run, and how much of it has run, from 0 to 1. */
interface ClockNow {
  remaining: number;
  /** Seconds since it started. */
  run: number;
  elapsed: number;
}

/** Where a clock stands at `now`. */
export function clockAt(clock: ActivityClock, now: number): ClockNow {
  const remaining = Math.max(0, Math.min(clock.length, clock.endsAt - now));
  return { remaining, run: clock.length - remaining, elapsed: 1 - remaining / clock.length };
}

/**
 * The last seconds of a timed choice — a city, a vote — in which a player
 * who has not made it is told what happens at zero: one is drawn for them.
 */
export const HURRY_SECONDS = 5;

/** The current phase's clock, ticking each second; null when the phase has none. */
export function useActivityClock(): ClockNow | null {
  const clock = useTeamActivityStore((s) => s.clock);
  const now = useNowSeconds();
  return clock ? clockAt(clock, now) : null;
}

/**
 * Whether the viewer is choosing their city on the globe: a selection phase
 * for the HQ is running and the server has no city of theirs yet.
 */
export function isChoosingOnGlobe(
  payload: Pick<TeamActivityPayload, 'phase_type' | 'phase_state' | 'complete'>,
  viewerId: string | undefined,
): boolean {
  if (payload.complete || payload.phase_type !== 'selection') return false;
  const state = payload.phase_state as Partial<SelectionState>;
  return state.subject === 'hq_city' && !(viewerId && state.selections?.[viewerId]);
}

// ── Where each phase is drawn ──────────────────────────────────────────────

/** What the team waits for once the player has done their part: the others to read, to answer, to celebrate. */
export type WaitKind = 'reading' | 'quiz' | 'achievement';

/** What floats at the screen's foot beside the team: a phase the player acts on beside the game, or a wait. */
export type FloatingSheet = 'selection' | 'speaking' | WaitKind | 'allDone';

/** Where the current phase is drawn: floating, over the scrim, or neither. */
export interface ActivityPlacement {
  floating: FloatingSheet | null;
  /** The vote and the quiz as dialogs, the result as a moment. */
  overScrim: 'voting' | 'results' | 'quiz' | null;
}

/**
 * Who a phase waits on, each with whether they are done: a quiz's players by
 * whether they have finished, a dialogue's or an achievement's by the phase's
 * own list (`all_players`, where a player who drops counts as done). No one,
 * for a phase that does not wait.
 */
export function waitList(phaseType: string | null, phaseState: Record<string, unknown>): Record<string, boolean> {
  switch (phaseType) {
    case 'quiz': {
      const players = (phaseState as unknown as Partial<QuizState>).players ?? {};
      return Object.fromEntries(Object.entries(players).map(([id, player]) => [id, player.complete]));
    }
    case 'synced_dialogue':
    case 'per_player_synced_dialogue':
    case 'earn_achievement':
      return (phaseState as unknown as Partial<DialogueState>).all_players ?? {};
    default:
      return {};
  }
}

/**
 * Where the overlay draws the current phase. Choosing a city and the speaking
 * turns float beside the team; the vote and the quiz are dialogs, the result a
 * moment, over the scrim. A wait floats once the player has done their part —
 * read the dialogue, answered the last question, seen their achievement
 * through — while anyone else is on the phase's list: alone, the phase ends as
 * the player finishes. A moderator sees none of it. Once the team is through
 * the session's last step while other teams are not, it is all done.
 */
export function activityPlacement(input: {
  moderator: boolean;
  active: boolean;
  complete: boolean;
  /** The session's last step is under way (the other teams may still be in theirs). */
  lastStepRunning: boolean;
  phaseType: string | null;
  phaseState: Record<string, unknown>;
  viewerId: string | undefined;
  /** The player has closed the phase's dialogue on this screen. */
  dialogueSeen: boolean;
}): ActivityPlacement {
  const none: ActivityPlacement = { floating: null, overScrim: null };
  const { phaseType, phaseState, viewerId } = input;
  if (input.moderator) return none;
  if (!input.active) return input.complete && input.lastStepRunning ? { floating: 'allDone', overScrim: null } : none;

  const othersOn = Object.keys(waitList(phaseType, phaseState)).some((id) => id !== viewerId);
  const wait = (kind: WaitKind): ActivityPlacement => (othersOn ? { floating: kind, overScrim: null } : none);
  switch (phaseType) {
    case 'selection':
      return { floating: 'selection', overScrim: null };
    case 'speaking_rounds':
      return { floating: 'speaking', overScrim: null };
    case 'voting':
    case 'results':
      return { floating: null, overScrim: phaseType };
    case 'quiz': {
      const mine = viewerId ? (phaseState as unknown as Partial<QuizState>).players?.[viewerId] : undefined;
      return mine && !mine.complete ? { floating: null, overScrim: 'quiz' } : wait('quiz');
    }
    case 'synced_dialogue':
    case 'per_player_synced_dialogue': {
      const seenBy = (phaseState as unknown as Partial<DialogueState>).seen_by ?? {};
      const seen = input.dialogueSeen || (viewerId !== undefined && seenBy[viewerId] === true);
      return seen ? wait('reading') : none;
    }
    case 'earn_achievement': {
      const { earned_by = {}, acknowledged_by = {} } = phaseState as unknown as Partial<AchievementState>;
      // An earner sees the celebration and its line first; the wait after.
      const earning = viewerId !== undefined && (earned_by[viewerId]?.length ?? 0) > 0 && !acknowledged_by[viewerId];
      return earning ? none : wait('achievement');
    }
    default:
      return none;
  }
}

// ── Store ──────────────────────────────────────────────────────────────────

/** Everything an activity holds, as it is before one starts and after it is put away. */
const IDLE = {
  active: false,
  phaseIndex: 0,
  phaseCount: 0,
  phaseType: null,
  phaseState: {},
  sharedData: {},
  complete: false,
  clock: null,
  globeSelectionModeActive: false,
  pendingPick: null,
  pickSent: false,
  voteSent: null,
  localDialogueSeen: false,
  previewMode: false,
} satisfies Partial<TeamActivityState>;

export const useTeamActivityStore = create<TeamActivityState>((set) => ({
  ...IDLE,

  setActivityState: (payload, viewerId, now) =>
    set((state) => {
      const samePhase = payload.phase_index === state.phaseIndex && payload.phase_type === state.phaseType;
      const choosing = isChoosingOnGlobe(payload, viewerId);
      return {
        active: !payload.complete,
        phaseIndex: payload.phase_index,
        phaseCount: payload.phase_count,
        phaseType: payload.phase_type,
        phaseState: payload.phase_state ?? {},
        sharedData: payload.shared_data ?? {},
        complete: payload.complete,
        clock: nextClock(state.clock, clockReading(payload, viewerId), now),
        globeSelectionModeActive: choosing,
        // A pick lives while its player is still choosing in the same phase.
        pendingPick: samePhase && choosing ? state.pendingPick : null,
        pickSent: samePhase && choosing ? state.pickSent : false,
        voteSent: samePhase ? state.voteSent : null,
        // The server's own state ends an admin's preview, so the actions are
        // the server's again rather than the preview's.
        previewMode: false,
        // Clear localDialogueSeen when the phase index advances (new phase started).
        localDialogueSeen:
          payload.phase_index === state.phaseIndex &&
          (payload.phase_type === 'synced_dialogue' || payload.phase_type === 'per_player_synced_dialogue')
            ? state.localDialogueSeen
            : false,
      };
    }),

  reset: () => set(IDLE),

  /**
   * Clears the completion flag without touching any other state.
   * Called when the session script step advances so that a stale `complete=true`
   * from the previous step's activity does not trigger the "waiting for
   * other teams" overlay during the next step.
   */
  resetCompletion: () => set({ complete: false }),

  markLocalDialogueSeen: () => set({ localDialogueSeen: true }),

  startQuizPreview: (quizId: string, localUserId: string) => {
    const quiz = loadQuiz(quizId);
    const first = quiz?.questions[0];
    if (!quiz || !first) {
      console.warn('[TeamActivity] startQuizPreview: no quiz, or none with a question:', quizId);
      return;
    }
    // Every question has its clock: the server refuses a quiz with a question
    // that has none (phase-quiz.go, CheckConfig), and these are its quizzes.
    const firstLimit = first.timeLimitSec;
    const phaseState: QuizState = {
      quiz_id: quizId,
      question_count: quiz.questions.length,
      players: {
        [localUserId]: { current_question_index: 0, answered_count: 0, complete: false, time_remaining: firstLimit, time_limit: firstLimit },
      },
    };
    set({
      ...IDLE,
      active: true,
      phaseCount: 1,
      phaseType: 'quiz',
      phaseState: { ...phaseState },
      clock: nextClock(null, previewClock(phaseState, localUserId), currentSeconds()),
      previewMode: true,
    });
  },

  stopPreview: () => set(IDLE),

  processPreviewAction: (action: Record<string, unknown>) =>
    set((state) => {
      if (!state.previewMode || state.phaseType !== 'quiz') return state;

      const phaseState = state.phaseState as unknown as QuizState;
      // The local player, the only one in a preview.
      const localUserId = Object.keys(phaseState.players)[0];
      if (!localUserId) return state;

      const player: QuizPlayerState = { ...phaseState.players[localUserId] };
      const index = player.current_question_index;
      const quiz = loadQuiz(phaseState.quiz_id);

      /** Puts the player on the next question, its clock whole, or done after the last. */
      const moveOn = () => {
        const next = quiz?.questions[index + 1];
        if (!next) {
          player.complete = true;
        } else {
          player.current_question_index = index + 1;
          player.time_remaining = next.timeLimitSec;
          player.time_limit = next.timeLimitSec;
          player.answered_count += 1;
        }
      };
      /**
       * The state the preview now holds, with its clock, as a state from the
       * server is taken. Alone in it, the player done is the quiz done: the
       * preview ends with its last answer, as the phase would.
       */
      const updated = (): Partial<TeamActivityState> => {
        if (player.complete) return IDLE;
        const next: QuizState = { ...phaseState, players: { ...phaseState.players, [localUserId]: player } };
        return { phaseState: { ...next }, clock: nextClock(state.clock, previewClock(next, localUserId), currentSeconds()) };
      };

      if (action.action === 'quiz_timeout') {
        // As on the server, a timeout counts only for the question and try it
        // names; it has no verdict, so the last one goes.
        if (action.question_index !== index || action.attempt !== currentTry(player)) return state;
        moveOn();
        player.last_answer_result = undefined;
        return updated();
      }

      if (action.action === 'quiz_answer') {
        const question = quiz?.questions[index];
        if (!question) return state;

        const answer = action.answer as string;
        const attempt = currentTry(player);
        const maxAttempts = triesFor(question);
        const graded = isGraded(question);

        // A free answer shorter than its minimum is not taken, as on the
        // server: the player stays on the question, with no verdict.
        if (question.type === 'free_text' && !longEnough(question, answer)) {
          return state;
        }
        // An opinion or a free answer is taken as given; a graded answer is
        // right or wrong.
        const correct = !graded || answer === question.correctOptionId;
        const verdict: QuizVerdict = { question_index: index, correct, attempt, max_attempts: maxAttempts };

        if (correct || attempt >= maxAttempts) {
          moveOn();
          // As on the server: a graded answer's verdict stays to be said before
          // the next question; an opinion or a free answer has none.
          player.last_answer_result = graded ? verdict : undefined;
        } else {
          // A wrong answer with a try left: the same question, its clock whole again.
          player.last_answer_result = verdict;
          player.time_remaining = question.timeLimitSec;
        }

        return updated();
      }

      return state;
    }),
}));

/** A preview's clock, read as the server's state is: its quiz phase, the only one. */
function previewClock(phaseState: QuizState, viewerId: string): ClockReading | null {
  return clockReading({ phase_type: 'quiz', phase_index: 0, phase_state: { ...phaseState }, complete: false }, viewerId);
}

// ── Handler ────────────────────────────────────────────────────────────────

/**
 * Handles an incoming OpCodeTeamActivity (330) message from the server.
 * Updates the store and, when the phase is synced_dialogue, triggers the
 * local dialogue display.
 */
export function handleTeamActivity(data: TeamActivityPayload): void {
  const store = useTeamActivityStore.getState();
  const prevPhaseType = store.phaseType;
  const prevPhaseIndex = store.phaseIndex;
  const viewerId = getViewerUserId();

  store.setActivityState(data, viewerId, currentSeconds());

  // When transitioning into a synced_dialogue phase, trigger the dialogue overlay.
  const isNewSyncedDialogue =
    data.phase_type === 'synced_dialogue' &&
    (prevPhaseType !== 'synced_dialogue' || prevPhaseIndex !== data.phase_index);

  if (isNewSyncedDialogue) {
    const dialogueId = data.phase_state?.dialogue_id as string | undefined;
    const dialogueVariables = data.phase_state?.dialogue_variables as Record<string, string> | undefined;
    if (dialogueId) {
      openDialogue(dialogueId, dialogueVariables);
    }
  }

  // When transitioning into a per_player_synced_dialogue phase, trigger the
  // dialogue for this specific player based on their entry in player_dialogues.
  const isNewPerPlayerDialogue =
    data.phase_type === 'per_player_synced_dialogue' &&
    (prevPhaseType !== 'per_player_synced_dialogue' || prevPhaseIndex !== data.phase_index);

  if (isNewPerPlayerDialogue) {
    const playerDialogues = data.phase_state?.player_dialogues as Record<string, string> | undefined;
    const dialogueVariables = data.phase_state?.dialogue_variables as Record<string, string> | undefined;
    const dialogueId = viewerId ? playerDialogues?.[viewerId] : undefined;
    if (dialogueId) {
      openDialogue(dialogueId, dialogueVariables);
    }
  }
}

/**
 * A dialogue closed (dialogue.ts): a dialogue phase counts the player as
 * having seen it, and the earn-achievement phase hears that the earner has
 * finished their flow — closing any dialogue then sends the acknowledgement.
 */
export function handleDialogueClosed(): void {
  const s = useTeamActivityStore.getState();
  if (!s.active) return;
  if (s.phaseType === 'synced_dialogue' || s.phaseType === 'per_player_synced_dialogue') s.markLocalDialogueSeen();
  if (s.phaseType === 'earn_achievement') void sendActivityAction({ action: 'achievement_acknowledged' });
}

/**
 * A session step started (match-session-script.ts): the last step's
 * completion goes, so the "waiting for other teams" overlay does not open at
 * the new step's start.
 */
export function handleStepStarted(): void {
  useTeamActivityStore.getState().resetCompletion();
}

// ── Derived selectors ──────────────────────────────────────────────────────

/**
 * Returns true when an activity speaking_rounds phase is active AND the given
 * userId is the current designated speaker. This means the activity is
 * overriding the server's voiceChatEnabled=false feature flag for this player,
 * and the UI should NOT show "voice chat disabled" indicators.
 */
export function useActivityMicGranted(userId: string | undefined): boolean {
  return useTeamActivityStore((s) => {
    if (!userId || !s.active || s.phaseType !== 'speaking_rounds') return false;
    const currentSpeakerId = s.phaseState.current_speaker_id as string | undefined;
    return !!currentSpeakerId && currentSpeakerId === userId;
  });
}

/**
 * Returns true when an activity speaking_rounds phase is active (for any player).
 * During speaking rounds, all players can hear — the server's voice-disabled flag
 * should not show the "voice disabled" banner or silence remotes.
 */
export function useActivitySpeakingRoundsActive(): boolean {
  return useTeamActivityStore((s) => s.active && s.phaseType === 'speaking_rounds');
}

/**
 * Who may talk now, from the session's voice switch and the team's activity:
 *
 *   open        anyone: voice is on, and no speaking round is running
 *   offForStep  no one: a script step has turned voice off
 *   rounds      one at a time: a speaking round gives the floor to each in
 *               turn, and everyone hears them, whatever the step says
 */
export type TalkState = 'open' | 'offForStep' | 'rounds';

export function talkState(voiceOn: boolean, speakingRounds: boolean): TalkState {
  if (speakingRounds) return 'rounds';
  return voiceOn ? 'open' : 'offForStep';
}

/** Who may talk now; see `talkState`. */
export function useTalkState(): TalkState {
  const voiceOn = useVoiceChatStore((s) => s.serverVoiceChatEnabled);
  return talkState(voiceOn, useActivitySpeakingRoundsActive());
}

/**
 * The verdict that is a try at the question on screen. The server keeps a
 * player's latest verdict after they move on, so it can be said before the
 * next question; one on the question before is not a try at this one.
 */
export function verdictForQuestion(verdict: QuizVerdict | undefined, questionIndex: number): QuizVerdict | undefined {
  return verdict?.question_index === questionIndex ? verdict : undefined;
}

// ── Action sender ──────────────────────────────────────────────────────────

/**
 * Sends a player action for the current activity phase to the server, and
 * resolves whether it went (sendToMatch): while the game reconnects nothing
 * does. e.g. sendActivityAction({ action: 'vote', value: 'GRU' })
 * During the admin's quiz preview, which stands in for the server, the
 * preview takes it instead.
 */
export function sendActivityAction(action: Record<string, unknown>): Promise<boolean> {
  const store = useTeamActivityStore.getState();
  if (store.previewMode) {
    store.processPreviewAction(action);
    return Promise.resolve(true);
  }
  return sendToMatch(OpCode.OpCodeTeamActivityAction, { type: MessageType.MessageTypeTeamActivityAction, ...action }, 'an activity action');
}

/**
 * Casts the player's vote for a city: sends it, and keeps it to show until
 * the vote closes — once it has gone. A vote that could not go, while the
 * game reconnects, is not shown as cast: the server never counted it, and
 * the player can cast it again once the game is back.
 */
export async function castVote(code: string): Promise<void> {
  if (useTeamActivityStore.getState().voteSent) return;
  useTeamActivityStore.setState({ voteSent: code });
  if (!(await sendActivityAction({ action: 'vote', value: code }))) {
    useTeamActivityStore.setState((state) => (state.voteSent === code ? { voteSent: null } : state));
  }
}

/**
 * A city clicked on the globe while the player chooses: their pick, waiting on
 * the sheet for them to confirm it (confirmPick) or click another. Only while
 * the globe is choosing and nothing has been sent.
 */
export function proposeCity(code: string): void {
  useTeamActivityStore.setState((state) => (state.globeSelectionModeActive && !state.pickSent ? { pendingPick: code } : state));
}

/**
 * Makes the player's pick their city: sends it, and holds the pick as sent
 * until the server lists it — the server keeps the first city a player sends,
 * so the confirm is what makes a choice final. A pick that could not go, as
 * a vote, is not held as sent.
 */
export async function confirmPick(): Promise<void> {
  const { pendingPick, pickSent } = useTeamActivityStore.getState();
  if (!pendingPick || pickSent) return;
  useTeamActivityStore.setState({ pickSent: true });
  if (!(await sendActivityAction({ action: 'select', value: pendingPick }))) {
    useTeamActivityStore.setState({ pickSent: false });
  }
}

/** Starts the admin's quiz preview: the quiz played alone, the preview standing in for the server. */
export function startQuizPreview(quizId: string, localUserId: string): void {
  useTeamActivityStore.getState().startQuizPreview(quizId, localUserId);
}

/** Ends the admin's quiz preview: the close on its notice. */
export function stopQuizPreview(): void {
  useTeamActivityStore.getState().stopPreview();
}
